'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import type { User as SupabaseUser } from '@supabase/supabase-js';
import { useAppStore } from '@/lib/store';
import type { User, UserRole } from '@/lib/types';
import { clearQueryCache } from '@/lib/providers';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

// Fetch user profile from user_profiles. Return null on failure.
// Add timeout to prevent hanging requests
// IMPORTANT: supabase client must be passed in to avoid creating multiple instances
async function fetchUserProfile(
  supabaseUser: SupabaseUser,
  supabase: ReturnType<typeof createClient>, // Use existing client instead of creating new one
  retries = 2,
  timeout = 10000 // 10 seconds timeout
): Promise<User | null> {

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    console.log(
      '[AuthProvider] Fetching user profile for:',
      supabaseUser.id,
      'attempt:',
      attempt + 1
    );

    try {
      console.log('[AuthProvider] About to execute Supabase query...');
      
      // Wrap query in try-catch to handle Navigator LockManager errors
      let queryResult: { data: any; error: any } | null = null;
      try {
        // Add timeout to prevent hanging requests
        const queryPromise = supabase
          .from('user_profiles')
          .select('id, email, role, name, is_active')
          .eq('id', supabaseUser.id)
          .single();
        
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => {
            reject(new Error(`Query timeout after ${timeout}ms`));
          }, timeout);
        });
        
        queryResult = await Promise.race([queryPromise, timeoutPromise]);
      } catch (queryError: any) {
        // Check if this is a Navigator LockManager error
        const errorMessage = queryError?.message || String(queryError) || '';
        const errorName = queryError?.name || '';
        const isLockError = 
          errorMessage.includes('NavigatorLockAcquireTimeoutError') ||
          errorMessage.includes('NavigatorLock') ||
          errorMessage.includes('lock:sb-') ||
          errorMessage.includes('Acquiring an exclusive Navigator LockManager lock') ||
          errorName === 'NavigatorLockAcquireTimeoutError' ||
          queryError?.constructor?.name === 'NavigatorLockAcquireTimeoutError';
        
        if (isLockError) {
          console.warn('[AuthProvider] Lock error during query, retrying without lock dependency...');
          // Retry immediately without waiting (lock errors are usually transient)
          // Use a shorter timeout for retry
          try {
            const retryPromise = supabase
              .from('user_profiles')
              .select('id, email, role, name, is_active')
              .eq('id', supabaseUser.id)
              .single();
            
            const retryTimeoutPromise = new Promise<never>((_, reject) => {
              setTimeout(() => {
                reject(new Error(`Retry query timeout after ${timeout}ms`));
              }, timeout);
            });
            
            queryResult = await Promise.race([retryPromise, retryTimeoutPromise]);
          } catch (retryError) {
            // If retry also fails, throw the original error
            throw queryError;
          }
        } else {
          // Re-throw non-lock errors
          throw queryError;
        }
      }
      
      if (!queryResult) {
        throw new Error('Query result is null');
      }
      
      const { data, error } = queryResult;
      console.log('[AuthProvider] Supabase query completed:', { hasData: !!data, hasError: !!error });

      if (!error && data) {
        console.log('[AuthProvider] User profile fetched successfully:', {
          id: data.id,
          role: data.role,
          is_active: data.is_active,
        });

        return {
          id: data.id,
          email: data.email || supabaseUser.email || '',
          role: (data.role as UserRole) || 'viewer',
          name: data.name || supabaseUser.user_metadata?.name || supabaseUser.email || undefined,
          is_active: data.is_active ?? false,
        };
      }

      console.error('[AuthProvider] Error fetching user profile:', {
        userId: supabaseUser.id,
        attempt: attempt + 1,
        error: error
          ? {
              message: error.message,
              code: error.code,
              details: error.details,
              hint: error.hint,
            }
          : 'No error object',
        hasData: !!data,
      });

      if (attempt < retries) {
        const delay = 500 * (attempt + 1);
        console.log(`[AuthProvider] Retrying in ${delay}ms...`);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    } catch (exception) {
      // Check if this is a React Suspense exception (error #460)
      // Suspense exceptions should be rethrown immediately
      if (exception && typeof exception === 'object' && 'then' in exception) {
        // This looks like a Promise/Suspense exception, rethrow it
        console.warn('[AuthProvider] Suspense exception detected, rethrowing...');
        throw exception;
      }

      console.error('[AuthProvider] Exception while fetching user profile:', {
        userId: supabaseUser.id,
        attempt: attempt + 1,
        exception: exception instanceof Error ? {
          message: exception.message,
          stack: exception.stack,
        } : String(exception),
      });

      if (attempt < retries) {
        const delay = 500 * (attempt + 1);
        console.log(`[AuthProvider] Retrying after exception in ${delay}ms...`);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  return null;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const { setUser: setStoreUser } = useAppStore();
  const supabase = createClient();

  useEffect(() => {
    console.log('[AuthProvider] Initializing auth state...');
    let isMounted = true;
    let profileFetchInProgress = false; // 防止并发获取 profile

    // 统一的 profile 获取函数（带防并发保护）
    const loadUserProfile = async (supabaseUser: SupabaseUser, source: string) => {
      if (profileFetchInProgress) {
        console.log(`[AuthProvider] Profile fetch already in progress, skipping ${source}`);
        return;
      }

      profileFetchInProgress = true;
      try {
        console.log(`[AuthProvider] Loading user profile from ${source}:`, supabaseUser.id);
        // 传入同一个 supabase client 实例，避免创建多个实例导致锁竞争
        const userProfile = await fetchUserProfile(supabaseUser, supabase);
        
        if (!isMounted) return;
        
        if (userProfile) {
          console.log(`[AuthProvider] User profile loaded from ${source}:`, {
            role: userProfile.role,
            is_active: userProfile.is_active,
          });
          setUser(userProfile);
          setStoreUser(userProfile);
        } else {
          console.warn(`[AuthProvider] User profile missing from ${source}`);
          setUser(null);
          setStoreUser(null);
        }
      } catch (error) {
        console.error(`[AuthProvider] Error loading profile from ${source}:`, error);
        if (isMounted) {
          setUser(null);
          setStoreUser(null);
        }
      } finally {
        profileFetchInProgress = false;
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    // 只使用 onAuthStateChange，监听 INITIAL_SESSION 事件
    // 这样可以避免 getSession() 和 onAuthStateChange 的并发问题
    // 注意：在 onAuthStateChange 回调中直接执行异步操作可能导致死锁
    // 使用 setTimeout 延迟异步操作，避免死锁问题
    let subscription: { unsubscribe: () => void } | null = null;
    try {
      const result = supabase.auth.onAuthStateChange((event, session) => {
        if (!isMounted) return;
        
        console.log('[AuthProvider] Auth state changed:', event);
        
        // 使用 setTimeout 延迟异步操作，避免在回调中直接执行导致死锁
        setTimeout(async () => {
          if (!isMounted) return;
          
          // 只在 INITIAL_SESSION 或 SIGNED_IN 时获取 profile
          // 避免在每次状态变化时都获取（减少并发）
          if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN') {
            if (session?.user) {
              await loadUserProfile(session.user, `onAuthStateChange(${event})`);
            } else {
              console.log('[AuthProvider] No user in session');
              if (isMounted) {
                setUser(null);
                setStoreUser(null);
                setLoading(false);
              }
            }
          } else if (event === 'SIGNED_OUT') {
            console.log('[AuthProvider] User signed out');
            if (isMounted) {
              setUser(null);
              setStoreUser(null);
              setLoading(false);
            }
          } else {
            // 其他事件（如 TOKEN_REFRESHED）不重新获取 profile
            console.log(`[AuthProvider] Event ${event} ignored for profile fetch`);
            if (isMounted && loading) {
              setLoading(false);
            }
          }
        }, 0); // 延迟到下一个事件循环
      });
      subscription = result.data.subscription;
    } catch (initError) {
      // 捕获 onAuthStateChange 初始化时的错误
      console.error('[AuthProvider] Error initializing onAuthStateChange:', initError);
      const errorMessage = (initError as any)?.message || String(initError) || '';
      const isLockError = 
        errorMessage.includes('NavigatorLockAcquireTimeoutError') ||
        errorMessage.includes('NavigatorLock') ||
        errorMessage.includes('lock:sb-');
      
      if (isLockError && isMounted) {
        console.warn('[AuthProvider] Lock error during onAuthStateChange init, falling back to getUser()');
        // 降级：使用 getUser() 作为备选方案
        supabase.auth.getUser()
          .then(async ({ data: { user: supabaseUser }, error: getUserError }) => {
            if (!isMounted || getUserError || !supabaseUser) {
              if (isMounted) {
                setUser(null);
                setStoreUser(null);
                setLoading(false);
              }
              return;
            }
            await loadUserProfile(supabaseUser, 'getUser() fallback');
          })
          .catch(() => {
            if (isMounted) {
              setUser(null);
              setStoreUser(null);
              setLoading(false);
            }
          });
      } else if (isMounted) {
        setUser(null);
        setStoreUser(null);
        setLoading(false);
      }
    }

    return () => {
      isMounted = false;
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [setStoreUser]);

  const signOut = async () => {
    // Clear local state first to avoid ProtectedRoute interference.
    setUser(null);
    setStoreUser(null);

    // Clear PWA query cache to prevent showing cached data for different user
    try {
      await clearQueryCache();
    } catch (error) {
      console.error('Failed to clear query cache on sign out:', error);
    }

    // Run sign out; keep it non-blocking.
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Error signing out from Supabase:', error);
      }
    } catch (error) {
      console.error('Failed to sign out:', error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}
